import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as inventoryApi from "@/lib/inventoryApi";

const errorText = err => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || err?.message || "The server did not answer";
};

/**
 * How suppliers sell an item, in its stock unit (owner brief 2026-11-01 §3.2):
 * "BTL 750" = 750 ML, "CASE" = 24 PCS. A bill line in a buy unit brings in
 * quantity × that many stock units. The default one is what a new bill line starts in.
 */
export default function BuyUnitsEditor({ item, token, canWrite }) {
  const [rows, setRows] = useState(null);
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let stale = false;
    inventoryApi.listBuyUnits(item.id, token)
      .then(list => { if (!stale) setRows(list.map(u => ({ name: u.name, factor: String(u.factor), is_default: u.is_default }))); })
      .catch(err => { if (!stale) setFailure(errorText(err)); });
    return () => { stale = true; };
  }, [item.id, token]);

  const set = (i, key, value) => setRows(list => list.map((r, j) => {
    if (key === "is_default") return { ...r, is_default: j === i ? value : false };
    return j === i ? { ...r, [key]: value } : r;
  }));

  const save = async () => {
    setSaving(true);
    setFailure(null);
    try {
      const saved = await inventoryApi.setBuyUnits(item.id, rows.map(r => ({
        name: r.name.trim(), factor: Number(r.factor), is_default: !!r.is_default })), token);
      setRows(saved.map(u => ({ name: u.name, factor: String(u.factor), is_default: u.is_default })));
      toast.success(`${item.name}: ${saved.length} buy unit(s) saved`);
    } catch (err) {
      setFailure(errorText(err));
      toast.error(`Buy units not saved — ${errorText(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <fieldset className="border rounded-lg p-base space-y-snug" data-testid="buy-units-editor">
      <legend className="text-meta font-medium text-muted-foreground px-tight">How suppliers sell it</legend>
      <p className="text-micro text-muted-foreground">
        A supplier's unit and how many {item.unit} it holds — BTL 750 = 750 {item.unit}. A bill can then be entered as it is printed.
      </p>
      {rows === null && !failure && <p className="text-meta text-muted-foreground">Reading…</p>}
      {rows && rows.length === 0 && <p className="text-meta text-muted-foreground" data-testid="buy-units-empty">None yet: bills for it are entered in {item.unit}.</p>}
      {(rows || []).map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-snug items-center" data-testid={`buy-unit-${i}`}>
          <Input className="col-span-5 h-11" placeholder="BTL 750" value={r.name} disabled={!canWrite}
                 onChange={e => set(i, "name", e.target.value)} data-testid={`buy-unit-${i}-name`} />
          <div className="col-span-4 flex items-center gap-tight">
            <span className="text-meta text-muted-foreground">=</span>
            <Input className="h-11" type="number" min="0" step="0.001" value={r.factor} disabled={!canWrite}
                   onFocus={e => e.target.select()} onChange={e => set(i, "factor", e.target.value)} data-testid={`buy-unit-${i}-factor`} />
            <span className="text-meta text-muted-foreground">{item.unit}</span>
          </div>
          <label className="col-span-2 flex items-center gap-tight text-meta">
            <input type="radio" name={`buy-default-${item.id}`} checked={!!r.is_default} disabled={!canWrite}
                   onChange={() => set(i, "is_default", true)} data-testid={`buy-unit-${i}-default`} />
            Default
          </label>
          {canWrite && (
            <Button type="button" variant="ghost" size="icon" className="col-span-1 h-11 w-11" aria-label={`Remove ${r.name || "buy unit"}`}
                    onClick={() => setRows(list => list.filter((_, j) => j !== i))}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      {failure && <p className="text-meta text-destructive" role="alert" data-testid="buy-units-error">{failure}</p>}
      {canWrite && rows && (
        <div className="flex gap-snug">
          <Button type="button" variant="outline" className="h-11" onClick={() => setRows(list => [...list, { name: "", factor: "", is_default: list.length === 0 }])}
                  data-testid="buy-unit-add-button">
            <Plus className="w-4 h-4 mr-tight" />Add a buy unit
          </Button>
          <Button type="button" variant="action" className="h-11" disabled={saving} onClick={save} data-testid="buy-units-save-button">
            {saving ? "Saving…" : "Save buy units"}
          </Button>
        </div>
      )}
    </fieldset>
  );
}
